import { Link, useParams } from "react-router-dom";
import { MapPin, Check, ArrowRight } from "lucide-react";
import { Seo, faqJsonLd, breadcrumbJsonLd } from "../../components/Seo";
import { ServiceIcon } from "../../components/ServiceIcon";
import { Faq } from "../../components/Faq";
import { CallCta } from "../../components/CallCta";
import { Testimonials } from "../../components/Testimonials";
import { getCityFr, nearbyCitiesFr, SERVICES_FR, BUSINESS_FR } from "../../data/site-fr";
import NotFound from "../NotFound";

const CITY_IMG = "https://images.unsplash.com/photo-1591658523269-4f48b4210c58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxXaW5uaXBlZyUyME1hbml0b2JhJTIwY2l0eXNjYXBlJTIwc2t5bGluZSUyMGFlcmlhbHxlbnwwfHx8fDE3OTIzNzQyNzZ8MA&ixlib=rb-4.1.0&q=85";

export default function VillePageFr() {
  const { slug } = useParams();
  const city = getCityFr(slug);
  if (!city) return <NotFound />;
  const nearby = nearbyCitiesFr(city.slug);
  const faqs = [
    { q: `Offrez-vous des gardiens de sécurité 24/7 à ${city.name}?`, a: `Oui. Notre répartition fonctionne jour et nuit, et nous assurons le gardiennage 24/7, les quarts de nuit et la réponse rapide aux alarmes à ${city.name} et dans la région ${city.region}.` },
    { q: `Quels services de sécurité sont offerts à ${city.name}?`, a: `Tous nos services sont offerts à ${city.name} : gardiens en uniforme, patrouille mobile, sécurité de chantier, sécurité événementielle, ronde incendie, sécurité conciergerie, prévention des pertes, sécurité industrielle, sécurité en milieu de santé, protection rapprochée et réponse aux alarmes.` },
    { q: `Comment embaucher une agence de sécurité à ${city.name}?`, a: `Appelez le ${BUSINESS_FR.phone} ou écrivez à ${BUSINESS_FR.email}. Décrivez-nous votre site à ${city.name} et nous vous recommanderons une couverture avec une soumission sans obligation.` },
  ];

  return (
    <div data-testid={`ville-fr-page-${city.slug}`}>
      <Seo
        lang="fr-CA"
        title={`Agence de Sécurité ${city.name} | 1st Nations Security`}
        description={`Gardiens de sécurité licenciés à ${city.name} — gardiennage, patrouille mobile, sécurité événementielle et de chantier. 24/7 : ${BUSINESS_FR.phone}.`}
        path={`/francais/villes/${city.slug}/`}
        jsonLd={[faqJsonLd(faqs), breadcrumbJsonLd([{ name: "Accueil", path: "/francais" }, { name: "Villes desservies", path: "/francais/villes" }, { name: city.name, path: `/francais/villes/${city.slug}` }])]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={CITY_IMG} alt={`${city.name} — zone de service de 1st Nations Security`} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0D0E11]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d'Ariane">
            <Link to="/francais" className="hover:text-[#FF9100]" data-testid="breadcrumb-fr-accueil">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/francais/villes" className="hover:text-[#FF9100]" data-testid="breadcrumb-fr-villes">Villes desservies</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">{city.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5 mb-5">
            <MapPin className="w-4 h-4 text-[#FF9100]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100]">{city.region} · Pop. {city.pop}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6 max-w-3xl">
            Services de Sécurité à <span className="text-[#FF9100]">{city.name}</span>
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mb-4">
            {city.desc} 1st Nations Security est une compagnie de sécurité détenue par des Autochtones qui offre une protection professionnelle et licenciée à {city.name} — appuyée par une répartition 24/7.
          </p>
          <p className="text-gray-400 leading-relaxed max-w-3xl">{city.local}</p>
        </div>
      </section>

      <section className="py-16" data-testid="ville-fr-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
            Services de Sécurité Offerts à {city.name}
          </h2>
          <p className="text-gray-400 max-w-3xl mb-8 leading-relaxed">
            Que vous ayez besoin de sécurité de chantier à {city.name}, de patrouilles mobiles dans la région {city.region}, de sécurité événementielle pour vos rassemblements ou d'une ronde incendie pendant une panne de système, un seul appel suffit.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES_FR.map((s) => (
              <Link
                key={s.slug}
                to={`/francais/services-de-securite/${s.slug}`}
                data-testid={`ville-fr-service-${s.slug}`}
                className="group flex items-center gap-4 bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-xl p-4 transition-all duration-200"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100]">
                  <ServiceIcon name={s.icon} className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white group-hover:text-[#FF9100] transition-colors">{s.title} {city.name}</p>
                  <p className="text-xs text-gray-500">{s.category}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0A0B0E]" data-testid="ville-fr-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">
              Pourquoi {city.name} Choisit 1st Nations Security
            </h2>
            <ul className="space-y-4">
              {[
                `Gardiens de sécurité licenciés avec une formation propre aux sites de ${city.name}`,
                "Entreprise détenue par des Autochtones — embauche locale et service respectueux de la culture",
                "Ligne de répartition 24/7 avec patrouille mobile et réponse aux alarmes",
                "Patrouilles vérifiées par GPS et rapports d'incident numériques après chaque quart",
                `Couverture flexible — de la sécurité événementielle d'un soir à ${city.name} aux gardiens postés en permanence`,
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-300" data-testid={`ville-fr-why-${i}`}>
                  <Check className="w-5 h-5 text-[#FF9100] mt-0.5 shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">
              Autres Villes Desservies
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed">
              En plus de {city.name}, nous offrons des services de gardiennage dans les grandes villes du Québec et partout au Manitoba.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/francais/villes/${n.slug}`}
                  data-testid={`ville-fr-nearby-${n.slug}`}
                  className="group flex items-center justify-between bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-lg px-4 py-3 transition-colors duration-200"
                >
                  <span className="text-sm text-gray-300 group-hover:text-[#FF9100] transition-colors">Sécurité {n.name}</span>
                  <ArrowRight className="w-4 h-4 text-[#FF9100] opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="ville-fr-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-8">
            Sécurité à {city.name} — FAQ
          </h2>
          <Faq items={faqs} testidPrefix={`${city.slug}-fr-faq`} />
        </div>
      </section>

      <Testimonials fr />
      <CallCta heading={`Embauchez des Gardiens de Sécurité à ${city.name}`} sub={`Appelez le ${BUSINESS_FR.phone} ou écrivez à ${BUSINESS_FR.email} — service 24/7 à ${city.name} et dans la région ${city.region}.`} testid="ville-fr-cta" />
    </div>
  );
}
